<template>
  <div class="import-wizard">
    <div class="stepper">
      <div class="stepper-header">
        <div :class="['step', currentStep >= 1 ? 'active' : '', currentStep > 1 ? 'completed' : '']">
          <div class="step-number">1</div>
          <div class="step-label">Upload CSV</div>
        </div>

        <div class="step-divider"></div>

        <div :class="['step', currentStep >= 2 ? 'active' : '', currentStep > 2 ? 'completed' : '']">
          <div class="step-number">2</div>
          <div class="step-label">Map Columns</div>
        </div>

        <div class="step-divider"></div>

        <!-- Conditional step: Map Session Types (only shown if SESSION_TYPE_NAME is mapped) -->
        <template v-if="showSessionTypeMappingStep">
          <div :class="['step', currentStep >= 3 ? 'active' : '', currentStep > 3 ? 'completed' : '']">
            <div class="step-number">3</div>
            <div class="step-label">Map Values</div>
          </div>

          <div class="step-divider"></div>

          <div :class="['step', currentStep >= 4 ? 'active' : '', currentStep > 4 ? 'completed' : '']">
            <div class="step-number">4</div>
            <div class="step-label">Preview & Import</div>
          </div>

          <div class="step-divider"></div>

          <div :class="['step', currentStep >= 5 ? 'active' : '']">
            <div class="step-number">5</div>
            <div class="step-label">Results</div>
          </div>
        </template>
        <template v-else>
          <div :class="['step', currentStep >= 3 ? 'active' : '', currentStep > 3 ? 'completed' : '']">
            <div class="step-number">3</div>
            <div class="step-label">Preview & Import</div>
          </div>

          <div class="step-divider"></div>

          <div :class="['step', currentStep >= 4 ? 'active' : '']">
            <div class="step-number">4</div>
            <div class="step-label">Results</div>
          </div>
        </template>
      </div>

      <div class="stepper-content">
        <!-- Step 1: Upload CSV -->
        <div v-if="currentStep === 1" class="step-content">
          <drive-file-uploader
            @file-selected="handleFileSelected"
            @uploaded="handleFileUploaded"
          />

          <div class="actions">
            <button
              class="btn btn-primary"
              :disabled="!fileUploaded"
              @click="nextStep"
            >
              Continue
            </button>
          </div>
        </div>

        <!-- Step 2: Map Columns -->
        <div v-if="currentStep === 2" class="step-content">
          <drive-column-mapper
            v-show="fileUploaded"
            :headers="csvHeaders"
            :preview-data="previewData"
            :available-fields="availableFields"
            ref="columnMapper"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button
              class="btn btn-primary"
              :disabled="!canProceedFromColumnMapping"
              :class="{ 'loading': analyzingFile }"
              @click="proceedFromColumnMapping"
            >
              <span v-if="analyzingFile">Analyzing...</span>
              <span v-else>{{ showSessionTypeMappingStep ? 'Map Session Types' : 'Preview Import' }}</span>
            </button>
          </div>
        </div>

        <!-- Step 3: Map Session Types (conditional) -->
        <div v-if="currentStep === 3 && showSessionTypeMappingStep" class="step-content">
          <drive-value-mapper
            :unique-values="uniqueSessionTypeValues"
            :available-options="availableSessionTypes"
            :mapping="sessionTypeMapping"
            @update:mapping="updateSessionTypeMapping"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button
              class="btn btn-primary"
              :disabled="!canProceedFromSessionTypeMapping"
              @click="nextStep"
            >
              Preview Import
            </button>
          </div>
        </div>

        <!-- Step 3/4: Preview & Import -->
        <div v-if="currentStep === previewStep" class="step-content">
          <drive-import-preview
            :preview-data="mappedPreviewData"
            :options="importOptions"
            @update:options="updateImportOptions"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button
              class="btn btn-primary"
              :class="{ 'loading': importing }"
              @click="startImport"
            >
              <span v-if="importing">Importing...</span>
              <span v-else>Start Import</span>
            </button>
          </div>
        </div>

        <!-- Step 4/5: Results -->
        <div v-if="currentStep === resultsStep" class="step-content">
          <drive-import-results
            :results="importResults"
            :status="importStatus"
            :progress="importProgress"
            :failure-reason="importFailureReason"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="resetWizard">
              Import Another File
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DriveFileUploader from './DriveFileUploader.vue';
import DriveColumnMapper from './DriveColumnMapper.vue';
import DriveValueMapper from './DriveValueMapper.vue';
import DriveImportPreview from './DriveImportPreview.vue';
import DriveImportResults from './DriveImportResults.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    DriveFileUploader,
    DriveColumnMapper,
    DriveValueMapper,
    DriveImportPreview,
    DriveImportResults
  },

  beforeDestroy() {
    // Clean up polling interval when component is destroyed
    this.stopPolling();
  },

  data() {
    return {
      fileId: null,
      // Polling configuration
      pollingInterval: null,
      pollAttempts: 0,
      maxPollAttempts: 300, // 10 minutes at 2s intervals
      pollIntervalMs: 2000, // 2 seconds
    }
  },

  computed: {
    ...mapGetters([
      'schoolId',
      'driveImportCurrentStep',
      'driveImportFileUploaded',
      'driveImportFileInfo',
      'driveImportPreviewData',
      'driveImportOptions',
      'driveImportImporting',
      'driveImportResults',
      'driveImportMappedPreviewData',
      'driveImportAvailableSessionTypes',
      'driveImportUniqueSessionTypeValues',
      'driveImportSessionTypeMapping',
      'driveImportShowSessionTypeMappingStep',
      'driveImportAnalyzingFile',
      'validateDriveImportSessionTypeMapping',
      'driveImportId',
      'driveImportStatus',
      'driveImportProgress',
      'driveImportFailureReason'
    ]),

    currentStep: {
      get() {
        return this.driveImportCurrentStep;
      },
      set(value) {
        this.$store.dispatch('SET_DRIVE_IMPORT_STEP', value);
      }
    },

    fileUploaded: {
      get() {
        return this.driveImportFileUploaded;
      },
      set(value) {
        this.$store.dispatch('SET_DRIVE_IMPORT_FILE_UPLOADED', value);
      }
    },

    csvHeaders() {
      return this.driveImportPreviewData.headers;
    },

    previewData() {
      return this.driveImportPreviewData.data;
    },

    availableFields() {
      return this.driveImportPreviewData.fields;
    },

    importOptions: {
      get() {
        return this.driveImportOptions;
      },
      set(value) {
        this.$store.dispatch('SET_DRIVE_IMPORT_OPTIONS', value);
      }
    },

    importing() {
      return this.driveImportImporting;
    },

    importResults() {
      return this.driveImportResults;
    },

    mappedPreviewData() {
      return this.driveImportMappedPreviewData;
    },

    // Session type mapping state
    showSessionTypeMappingStep() {
      return this.driveImportShowSessionTypeMappingStep;
    },

    availableSessionTypes() {
      return this.driveImportAvailableSessionTypes;
    },

    uniqueSessionTypeValues() {
      return this.driveImportUniqueSessionTypeValues;
    },

    sessionTypeMapping() {
      return this.driveImportSessionTypeMapping;
    },

    analyzingFile() {
      return this.driveImportAnalyzingFile;
    },

    importStatus() {
      return this.driveImportStatus;
    },

    importProgress() {
      return this.driveImportProgress;
    },

    importFailureReason() {
      return this.driveImportFailureReason;
    },

    // Dynamic step numbers based on whether session type mapping step is shown
    previewStep() {
      return this.showSessionTypeMappingStep ? 4 : 3;
    },

    resultsStep() {
      return this.showSessionTypeMappingStep ? 5 : 4;
    },

    // Validation computed properties
    canProceedFromColumnMapping() {
      const mappedFields = Object.values(this.$store.state.driveImport.columnMapping);
      const requiredFields = this.$store.state.driveImport.requiredFields;
      return requiredFields.every((field) => mappedFields.includes(field));
    },

    canProceedFromSessionTypeMapping() {
      return this.validateDriveImportSessionTypeMapping;
    },

    canProceed() {
      if (this.currentStep === 1) {
        return this.fileUploaded;
      }

      if (this.currentStep === 2) {
        return this.canProceedFromColumnMapping;
      }

      return true;
    }
  },

  methods: {
    nextStep() {
      const maxStep = this.showSessionTypeMappingStep ? 5 : 4;
      if (this.currentStep < maxStep) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    resetWizard() {
      this.$store.dispatch('RESET_DRIVE_IMPORT');
    },

    handleFileSelected(file) {
      // Handle file selection logic
    },

    handleFileUploaded(response) {
      this.fileUploaded = true;
      this.fileId = response.file.id;

      this.$store.dispatch('SET_DRIVE_IMPORT_FILE_INFO', {
        directory: response.file.directory,
        name: response.file.name
      });

      // Fetch preview data
      this.$store.dispatch('PREVIEW_DRIVE_IMPORT_FILE', {school_id: this.schoolId, file_id: response.file.id})
      .then(response => {
        this.$store.dispatch('SET_DRIVE_IMPORT_PREVIEW_DATA', {
          headers: response.data.headers,
          data: response.data.sample_data,
          fields: response.data.available_fields
        });

        // Set required fields
        const requiredFields = response.data.available_fields
          .filter(field => field.required)
          .map(field => field.value);
        this.$store.dispatch('SET_DRIVE_IMPORT_REQUIRED_FIELDS', requiredFields);

        // Store available session types from the preview response
        if (response.data.session_types) {
          this.$store.dispatch('SET_DRIVE_IMPORT_AVAILABLE_SESSION_TYPES', response.data.session_types);
        }

        this.nextStep();
      })
      .catch(error => {
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Failed to load preview: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      });
    },

    updateImportOptions(options) {
      this.importOptions = { ...options };
    },

    updateSessionTypeMapping(mapping) {
      this.$store.dispatch('SET_DRIVE_IMPORT_SESSION_TYPE_MAPPING', mapping);
    },

    proceedFromColumnMapping() {
      const columnMapping = this.$store.state.driveImport.columnMapping;
      const mappedFields = Object.values(columnMapping);

      // Check if SESSION_TYPE_NAME is mapped
      const sessionTypeMapped = mappedFields.includes('SESSION_TYPE_NAME');

      if (sessionTypeMapped) {
        // Call analyze endpoint to get unique session type values
        this.$store.dispatch('ANALYZE_DRIVE_IMPORT_FILE', {
          school_id: this.schoolId,
          file_id: this.fileId,
          column_mapping: columnMapping
        })
        .then(response => {
          const uniqueValues = response.data.unique_values;
          const sessionTypeValues = uniqueValues['SESSION_TYPE_NAME'] || [];

          if (sessionTypeValues.length > 0) {
            // Store the unique values and show the mapping step
            this.$store.dispatch('SET_DRIVE_IMPORT_UNIQUE_SESSION_TYPE_VALUES', sessionTypeValues);
            this.$store.dispatch('SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP', true);
            this.nextStep(); // Go to step 3 (session type mapping)
          } else {
            // No unique values, skip to preview
            this.$store.dispatch('SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP', false);
            this.nextStep(); // Go to step 3 (preview)
          }
        })
        .catch(error => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'error',
            message: 'Failed to analyze file: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
          });
        });
      } else {
        // SESSION_TYPE_NAME not mapped, skip to preview
        this.$store.dispatch('SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP', false);
        this.nextStep();
      }
    },

    startImport() {
      this.$store.dispatch('SET_DRIVE_IMPORT_IMPORTING', true);

      // Prepare session type mapping for the API
      // Convert "skip" values to null for the backend
      const sessionTypeMapping = {};
      const rawMapping = this.$store.state.driveImport.sessionTypeMapping;

      Object.keys(rawMapping).forEach(key => {
        const value = rawMapping[key];
        if (value === 'skip') {
          sessionTypeMapping[key] = null;
        } else {
          sessionTypeMapping[key] = value;
        }
      });

      this.$store.dispatch('START_DRIVE_IMPORT', {
        school_id: this.schoolId,
        file_id: this.fileId,
        test: this.importOptions.test,
        column_mapping: this.$store.state.driveImport.columnMapping,
        session_type_mapping: this.showSessionTypeMappingStep ? sessionTypeMapping : undefined
      })
      .then(response => {
        // Check if this is an async response (202 with import_id)
        if (response.status === 202 && response.data.import_id) {
          // Async import - store the import_id and start polling
          this.$store.dispatch('driveImport/SET_DRIVE_IMPORT_ID', response.data.import_id);
          this.$store.dispatch('driveImport/SET_DRIVE_IMPORT_STATUS', 'pending');
          this.nextStep(); // Move to results step to show progress
          this.startPolling(response.data.import_id);
        } else {
          // Synchronous response (test mode) - results returned immediately
          this.$store.dispatch('SET_DRIVE_IMPORT_RESULTS', response.data);
          this.$store.dispatch('driveImport/SET_DRIVE_IMPORT_STATUS', 'completed');
          this.$store.dispatch('SET_DRIVE_IMPORT_IMPORTING', false);
          this.nextStep();
        }
      })
      .catch(error => {
        this.$store.dispatch('SET_DRIVE_IMPORT_IMPORTING', false);
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Import failed: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      });
    },

    startPolling(importId) {
      this.pollAttempts = 0;
      this.pollingInterval = setInterval(() => {
        this.pollImportStatus(importId);
      }, this.pollIntervalMs);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    pollImportStatus(importId) {
      this.pollAttempts++;

      // Safety check: stop polling after max attempts to prevent infinite loop
      if (this.pollAttempts >= this.maxPollAttempts) {
        this.stopPolling();
        this.$store.dispatch('SET_DRIVE_IMPORT_IMPORTING', false);
        this.$store.dispatch('driveImport/SET_DRIVE_IMPORT_STATUS', 'failed');
        this.$store.dispatch('driveImport/SET_DRIVE_IMPORT_FAILURE_REASON', 'Import timed out. Please check the import status manually or contact support.');
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Import polling timed out after 10 minutes.'
        });
        return;
      }

      this.$store.dispatch('driveImport/GET_DRIVE_IMPORT_STATUS', {
        school_id: this.schoolId,
        import_id: importId
      })
      .then(response => {
        const status = response.data.status;

        if (status === 'completed' || status === 'failed') {
          this.stopPolling();
          this.$store.dispatch('SET_DRIVE_IMPORT_IMPORTING', false);

          if (status === 'completed') {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'success',
              message: 'Import completed successfully!'
            });
          } else {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'error',
              message: 'Import failed: ' + (response.data.failure_reason || 'Unknown error')
            });
          }
        }
      })
      .catch(error => {
        // On network error, don't immediately stop - allow some retries
        console.error('Polling error:', error);

        // Stop polling if we get a 404 (import not found) or other client error
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          this.stopPolling();
          this.$store.dispatch('SET_DRIVE_IMPORT_IMPORTING', false);
          this.$store.dispatch('driveImport/SET_DRIVE_IMPORT_STATUS', 'failed');
          this.$store.dispatch('driveImport/SET_DRIVE_IMPORT_FAILURE_REASON', 'Unable to retrieve import status.');
        }
      });
    }
  },
}
</script>

<style scoped>
.import-wizard {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell,
    'Open Sans', 'Helvetica Neue', sans-serif;
  max-width: 1200px;
  margin: 0 auto;
}

.stepper {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stepper-header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  color: #757575;
}

.step.active {
  color: #1976d2;
}

.step.completed {
  color: #4caf50;
}

.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #fff;
  margin-bottom: 8px;
}

.step.active .step-number {
  background-color: #1976d2;
}

.step.completed .step-number {
  background-color: #4caf50;
}

.step-divider {
  height: 1px;
  background-color: #e0e0e0;
  flex: 1;
  margin: 0 8px;
}

.stepper-content {
  padding: 24px;
}

.step-content {
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  transition: background-color 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #1976d2;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1565c0;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.loading {
  position: relative;
  pointer-events: none;
}

.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1em;
  height: 1em;
  border-radius: 50%;
  border: 2px solid #fff;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  transform: translate(-50%, -50%);
}

@keyframes spin {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>
